import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, Card, CardBody, Button } from 'reactstrap';
import { MDBDataTable } from 'mdbreact';
import SweetAlert from 'react-bootstrap-sweetalert';
import { activateAuthLayout } from '../../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { formatTimestamp } from '../../../../helpers/common';

const QUOTE_TEMPLATES = gql`
    query getQuoteTemplates {
        QuoteTemplates {
            QuoteId
            ProposalTitle
            Description
            Created
        }
    }
`;

const DELETE_QUOTE_TEMPLATE = gql`
    mutation deleteQuoteTemplate($QuoteId: ID!) {
        deleteQuoteTemplate(QuoteId: $QuoteId) {
            status
            message
        }
    }
`;

const UPDATE_QUOTE = gql`
    mutation updateQuote($QuoteInput: QuoteInput) {
        updateQuote(input: $QuoteInput) {
            QuoteId
            WorkflowStep
        }
    }
`;

const columns = [
    { label: ['Template Name', <i key="title" className="fa fa-sort ml-2"></i>], field: 'ProposalTitle', sort: 'asc' },
    { label: ['Description', <i key="desc" className="fa fa-sort ml-2"></i>], field: 'Description', sort: 'asc' },
    { label: ['Created', <i key="created" className="fa fa-sort ml-2"></i>], field: 'Created', sort: 'asc' },
    { label: 'Action', field: 'Action', sort: 'disabled' },
];

const QuoteTemplateList = ({ activateAuthLayout }) => {
    useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

    const history = useHistory();
    const [tableData, setTableData] = useState({ columns, rows: [] });
    const [deleteId, setDeleteId] = useState(null);

    const { data, refetch } = useQuery(QUOTE_TEMPLATES);
    const [deleteQuoteTemplate] = useMutation(DELETE_QUOTE_TEMPLATE);
    const [updateQuote] = useMutation(UPDATE_QUOTE);

    const editTemplate = useCallback(async (e, quoteId) => {
        e.preventDefault();
        await updateQuote({ variables: { QuoteInput: { QuoteId: parseInt(quoteId, 10), WorkflowStep: 'QUOTE_SETUP' } } });
        history.push(`/template-setup/${quoteId}`);
    }, [history, updateQuote]);

    const confirmDelete = async () => {
        await deleteQuoteTemplate({ variables: { QuoteId: deleteId } });
        setDeleteId(null);
        refetch();
    };

    useEffect(() => {
        if (data) {
            const rows = data.QuoteTemplates.map((template) => ({
                ProposalTitle: template.ProposalTitle,
                Description: template.Description || '—',
                Created: <span searchvalue={template.Created}>{formatTimestamp(template.Created)}</span>,
                Action: (
                    <div>
                        <Button color="info" size="sm" className="mr-2" onClick={(e) => editTemplate(e, template.QuoteId)}>Edit</Button>
                        <Button color="danger" size="sm" onClick={() => setDeleteId(template.QuoteId)}>Delete</Button>
                    </div>
                ),
            }));

            setTableData({ columns, rows });
        }
    }, [data, editTemplate]);

    return (
        <React.Fragment>
            <div className="content">
                <div className="container-fluid">
                    <div className="page-title-box">
                        <Row className="align-items-center">
                            <Col sm="6">
                                <h4 className="page-title">Quote Templates</h4>
                            </Col>
                            <Col sm="6" className="text-right">
                                <Button color="success" onClick={() => history.push('/template-setup')}>
                                    Create New Template
                                </Button>
                            </Col>
                        </Row>
                    </div>

                    {deleteId && (
                        <SweetAlert
                            warning
                            title="Confirm"
                            onConfirm={confirmDelete}
                            onCancel={() => setDeleteId(null)}
                            showCancel
                            confirmBtnText="Delete"
                            cancelBtnText="Cancel"
                            confirmBtnBsStyle="danger"
                            cancelBtnBsStyle="secondary"
                        >
                            Are you sure you want to delete this template?
                        </SweetAlert>
                    )}

                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody>
                                    {data && (
                                        <MDBDataTable
                                            sortable
                                            striped
                                            borderless
                                            theadColor="table-dark"
                                            hover
                                            responsive
                                            noBottomColumns
                                            noRecordsFoundLabel="No templates found"
                                            data={tableData}
                                            sortRows={['Created']}
                                            order={['Created', 'desc']}
                                        />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
        </React.Fragment>
    );
};

export default connect(null, { activateAuthLayout })(QuoteTemplateList);
